import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  CreateBusinessPayload,
  UpdateBusinessPayload,
  SubmitRatingPayload,
  BusinessRating,
  BusinessRatingsSummary,
} from "../../interfaces/BusinessObject";
import {
  convertRawBusinessList,
  convertRawBusiness,
  convertRawBusinessRating,
  convertRawBusinessRatingsSummary,
} from "../converters/backendToUi/businessConvertor";
import {
  convertRawBusinessCollection,
  convertRawBusinessCollectionList,
  convertRawCollectionDetail,
} from "../converters/backendToUi/businessCollectionConvertor";
import {
  buildCreateCollectionPayload,
  buildUpdateCollectionPayload,
} from "../converters/uiToBackend/BusinessCollectionConvertor";
import {
  CollectionErrorCodes,
  CollectionErrorEnvelope,
  CreateCollectionPayload,
  UpdateCollectionPayload,
} from "../../interfaces/BusinessCollectionObject";
import { convertRawProduct } from "../converters/backendToUi/productConvertor";
import { convertRawMediaList } from "../converters/backendToUi/mediaConvertor";
import { GALLERY_THUMBNAIL_WIDTH, GALLERY_THUMBNAIL_HEIGHT, MediaPersistItem, MediaRecord } from "../../interfaces/MediaObject";
import {
  extractApiErrorMessage as extractErrorMessage,
  fetchOrThrowFriendly,
} from "../helpers/ApiErrorHelper";
import AnonymousRatingHelper from "../helpers/AnonymousRatingHelper";
import { buildCreateBusinessPayload, buildUpdateBusinessPayload } from "../converters/uiToBackend/BusinessConvertor";
import ProductConvertor from "../converters/uiToBackend/ProductConvertor";
import SearchConvertor from "../converters/uiToBackend/SearchConvertor";
import {
  BusinessMoreDetails,
  BusinessProfile,
} from "../../interfaces/BusinessObject";
import { UserPreferences } from "../../interfaces/UserObject";
import { getRequest, postRequest, putRequest, deleteRequest } from "../http/httpRequest";
import {
  dummyBusinessListings,
  dummyUserBusinesses,
  dummyPublicBusinesses,
} from "./dummyResponses/BusinessDummyResponse";

// ─── Swap each method body for a real httpRequest call when backend is ready ───

async function getMyBusinesses() {
  const endpoint = "/api/v1/business/my-businesses";
  const includeBearerToken = true;
  const response = await getRequest(endpoint, includeBearerToken);
  const data = await response.json();
  return convertRawBusinessList(data);
}

// Search businesses by query + location. Same request shape as profile/product
// search (see SearchConvertor); returns the standard business list envelope.
async function businessSearch(
  searchString: string,
  userPreferences: UserPreferences
) {
  const endpoint = "/api/v1/business/business-search";
  const payload = SearchConvertor.buildSearchPayload(searchString, userPreferences);
  const includeBearerToken = false;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  if (!response.ok) {
    throw new Error(`Business search failed: ${response.status}`);
  }
  const data = await response.json();
  return convertRawBusinessList(data);
}

/**
 * Business-collection error envelope. These endpoints report failures inside a
 * 2xx body ({errorInformation:[{errorCode}], generalProcessingStatus}), so a
 * response.ok check alone isn't enough. Codes: see CollectionErrorCodes.
 */
function throwOnCollectionFailure(data: unknown, fallback: string): void {
  if (!data || typeof data !== "object") return;
  const envelope = data as CollectionErrorEnvelope;

  const info = Array.isArray(envelope.errorInformation)
    ? envelope.errorInformation
    : [];
  const status = envelope.generalProcessingStatus?.processingStatus;
  const failed =
    info.length > 0 ||
    (typeof status === "string" && !["OK", "SUCCESS"].includes(status.toUpperCase()));
  if (!failed) return;

  const code = info[0]?.errorCode;
  switch (code) {
    case CollectionErrorCodes.NOT_FOUND:
      throw collectionError("We couldn't find that collection.", code);
    case CollectionErrorCodes.NAME_REQUIRED:
      throw collectionError("Enter a collection name to search.", code);
    case CollectionErrorCodes.INTERNAL:
      throw collectionError(
        "Something went wrong on our end. Please try again.",
        code
      );
    default:
      throw collectionError(extractErrorMessage(data) || fallback, code);
  }
}

/** Errors carry `code` through Redux Toolkit's miniSerializeError (which
 *  preserves name/message/stack/code), so `.unwrap()` callers can branch on it. */
function collectionError(message: string, code?: string): Error {
  const err = new Error(message);
  if (code) (err as Error & { code?: string }).code = code;
  return err;
}

// Collections matching a name (partial match handled by the backend).
async function searchCollectionsByName(collectionName: string) {
  const endpoint =
    `${ServiceEndpoints.BUSINESS_COLLECTIONS_BASE}?name=` +
    encodeURIComponent(collectionName);
  const includeBearerToken = false;
  const response = await fetchOrThrowFriendly(() =>
    getRequest(endpoint, includeBearerToken)
  );
  if (!response.ok) {
    throw new Error(
      await statusToMessage(response, "Couldn't search collections")
    );
  }
  const data = await response.json();
  throwOnCollectionFailure(data, "Couldn't search collections");
  return convertRawBusinessCollectionList(data);
}

/**
 * One collection plus its members, in a single call.
 *
 * `includeBusinessDetails` controls how much of each member comes back: false
 * returns businessId only (everything else null), true returns complete
 * business records. The collection page needs the grid, so it asks for true.
 */
async function getCollectionById(
  collectionId: string,
  includeBusinessDetails = true
) {
  const endpoint =
    `${ServiceEndpoints.BUSINESS_COLLECTIONS_BASE}/` +
    encodeURIComponent(collectionId) +
    "?includeBusinessDetails=" +
    String(includeBusinessDetails);
  const includeBearerToken = false;
  const response = await fetchOrThrowFriendly(() =>
    getRequest(endpoint, includeBearerToken)
  );
  if (!response.ok) {
    if (response.status === 404) {
      throw collectionError(
        "We couldn't find that collection.",
        CollectionErrorCodes.NOT_FOUND
      );
    }
    throw new Error(
      await statusToMessage(response, "Couldn't load this collection")
    );
  }
  const data = await response.json();
  throwOnCollectionFailure(data, "Couldn't load this collection");

  const detail = convertRawCollectionDetail(data);
  // A SUCCESS envelope with an empty `contents` still means "no such collection".
  if (!detail) {
    throw collectionError(
      "We couldn't find that collection.",
      CollectionErrorCodes.NOT_FOUND
    );
  }
  return detail;
}

/**
 * A lead's own collections — `creatorId` filter on the same base endpoint as
 * the name search (confirmed with backend). Bearer token identifies the
 * caller, but `creatorId` is still sent explicitly since the base GET is
 * otherwise name-scoped only.
 */
async function getMyCollections(creatorId: string) {
  const endpoint =
    `${ServiceEndpoints.BUSINESS_COLLECTIONS_BASE}?creatorId=` +
    encodeURIComponent(creatorId);
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() =>
    getRequest(endpoint, includeBearerToken)
  );
  if (!response.ok) {
    throw new Error(
      await statusToMessage(response, "Couldn't load your collections")
    );
  }
  const data = await response.json();
  throwOnCollectionFailure(data, "Couldn't load your collections");
  return convertRawBusinessCollectionList(data);
}

async function createCollection(payload: CreateCollectionPayload) {
  const body = buildCreateCollectionPayload(payload);
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() =>
    postRequest(ServiceEndpoints.BUSINESS_COLLECTIONS_BASE, body, includeBearerToken)
  );
  if (!response.ok) {
    throw new Error(
      await statusToMessage(response, "Couldn't create this collection")
    );
  }
  const data = await response.json();
  throwOnCollectionFailure(data, "Couldn't create this collection");
  const created = coerceContentsSingle(data);
  if (!created) {
    throw collectionError("Couldn't create this collection.");
  }
  return convertRawBusinessCollection(created);
}

async function updateCollection(payload: UpdateCollectionPayload) {
  const endpoint =
    `${ServiceEndpoints.BUSINESS_COLLECTIONS_BASE}/` +
    encodeURIComponent(payload.uid);
  const body = buildUpdateCollectionPayload(payload);
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() => putRequest(endpoint, body, includeBearerToken));
  if (!response.ok) {
    throw new Error(
      await statusToMessage(response, "Couldn't update this collection")
    );
  }
  const data = await response.json();
  throwOnCollectionFailure(data, "Couldn't update this collection");
  const updated = coerceContentsSingle(data);
  if (!updated) {
    throw collectionError("Couldn't update this collection.");
  }
  return convertRawBusinessCollection(updated);
}

async function deleteCollection(collectionId: string): Promise<void> {
  const endpoint =
    `${ServiceEndpoints.BUSINESS_COLLECTIONS_BASE}/` +
    encodeURIComponent(collectionId);
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() => deleteRequest(endpoint, includeBearerToken));
  if (!response.ok) {
    if (response.status === 404) {
      throw collectionError(
        "We couldn't find that collection.",
        CollectionErrorCodes.NOT_FOUND
      );
    }
    throw new Error(
      await statusToMessage(response, "Couldn't delete this collection")
    );
  }
  // Mirrors deleteBusinessMedia: a 2xx body can still carry a failure envelope.
  const data = await response.json().catch(() => null);
  if (data) throwOnCollectionFailure(data, "Couldn't delete this collection");
}

/** `contents` on these endpoints is a single-element array on success. */
function coerceContentsSingle(data: unknown): Record<string, unknown> | null {
  const contents = (data as { contents?: unknown[] } | null)?.contents;
  const first = Array.isArray(contents) ? contents[0] : null;
  return first && typeof first === "object"
    ? (first as Record<string, unknown>)
    : null;
}

async function getBusinessById(businessId: string) {
  const endpoint = "/api/v1/business/get-business-by-businessId/" + businessId;
  const includeBearerToken = true;
  const response = await getRequest(endpoint, includeBearerToken);
  const data = await response.json();
  return convertRawBusiness(data.contents[0]);
}

async function getBusinessListings(businessId: string, ignoreIds: string[] = []) {
  // MOCK — replace with:
  // const res = await postRequest(`${ServiceEndpoints.BUSINESS_BY_ID}/${businessId}/listings`, { ignoreIds }, false);
  // const raw = await res.json() as { products: Record<string, unknown>[]; totalCount: number };
  // return { products: raw.products.map(convertRawProduct), total_count: raw.totalCount };
  const data = dummyBusinessListings[businessId];
  if (!data) return { products: [], total_count: 0 };
  // Embedded product objects remain snake_case (product API contract is unchanged),
  // so we filter on `product_id` here even though the outer envelope is camelCase.
  const filtered = data.products.filter(
    (p) => !ignoreIds.includes((p as Record<string, unknown>).product_id as string)
  );
  return { products: filtered.map(convertRawProduct), total_count: data.totalCount };
}

// ─── Gallery media (images / videos / PDFs) ──────────────────────────────────
// Uploads go through the presigned-storage flow (see StorageApiService +
// useMediaUpload). These functions cover the persist + read + delete steps; the
// actual byte upload is owned by the generic upload engine.

/**
 * Reads the business gallery as normalized MediaRecords (type + path +
 * permanent publicUrl + optional section). Tolerates both the legacy
 * string-URL response and the richer object response (see convertRawMediaList).
 */
async function getBusinessMedia(businessId: string): Promise<MediaRecord[]> {
  const endpoint = `/api/v1/business-images/${businessId}/section-wise-gallery`;
  const response = await getRequest(endpoint, true);
  if (!response.ok) {
    throw new Error(await statusToMessage(response, "Failed to load gallery"));
  }
  const data = await response.json().catch(() => null);
  return convertRawMediaList(data?.contents);
}

/**
 * Appends already-uploaded media to the business gallery. Takes the records the
 * upload engine produced and persists {mediaType, objectPath, section?} per the
 * PUT /business-images/{id}/update contract.
 */
async function appendBusinessMedia(
  businessId: string,
  records: MediaRecord[]
): Promise<void> {
  const endpoint = `/api/v1/business-images/${businessId}/update`;
  const payload: MediaPersistItem[] = records.map((r) => ({
    mediaType: r.mediaType,
    objectPath: r.objectPath,
    ...(r.section ? { section: r.section } : {}),
    thumbnailWidth: GALLERY_THUMBNAIL_WIDTH,
    thumbnailHeight: GALLERY_THUMBNAIL_HEIGHT,
  }));
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() => putRequest(endpoint, payload, includeBearerToken));
  if (!response.ok) {
    throw new Error(await statusToMessage(response, "Failed to save media"));
  }
  // The backend answers 200 even when the update fails, reporting the real
  // outcome in the body envelope — so a 2xx alone doesn't mean the records
  // persisted. Inspect the envelope and surface the failure.
  const data = await response.json().catch(() => null);
  const failure = envelopeFailureMessage(data);
  if (failure) throw new Error(failure);
}

/**
 * Detects a failure reported inside a 2xx body. Handles both the new
 * {errors, status} envelope used by /business-images and the legacy
 * {errorInformation, generalProcessingStatus} envelope. Returns a user-facing
 * message, or null when the body looks like success (or carries no
 * recognizable envelope — the gallery read-back in useBusinessGallery covers
 * that case).
 */
function envelopeFailureMessage(data: any): string | null {
  if (!data || typeof data !== "object") return null;
  const fallback = "Couldn't save to the gallery. Please try again.";
  if (Array.isArray(data.errors) && data.errors.length) {
    return data.errors[0]?.errorDescription || fallback;
  }
  if (Array.isArray(data.errorInformation) && data.errorInformation.length) {
    return data.errorInformation[0]?.errorDetail || fallback;
  }
  const ps = data.generalProcessingStatus?.processingStatus;
  if (typeof ps === "string" && ps !== "OK" && ps !== "SUCCESS") {
    return data.generalProcessingStatus?.processingDetails || fallback;
  }
  const status = data.status;
  if (typeof status === "string" && !["OK", "SUCCESS"].includes(status.toUpperCase())) {
    return fallback;
  }
  return null;
}

/**
 * Deletes one gallery item by its storage object path. MediaRecords carry the
 * objectPath directly, so no URL parsing is needed.
 */
async function deleteBusinessMedia(
  businessId: string,
  objectPath: string
): Promise<void> {
  if (!objectPath) throw new Error("Couldn't determine the file to delete.");
  const endpoint =
    `/api/v1/business-images/${businessId}/gallery?image=${encodeURIComponent(objectPath)}`;
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() => deleteRequest(endpoint, includeBearerToken));
  if (!response.ok) {
    throw new Error(await statusToMessage(response, "Failed to delete media"));
  }
  // When the backend has no matching route, CloudFront falls through to the
  // SPA fallback and answers 200 with index.html — so a 2xx alone doesn't
  // mean the delete happened. An HTML body means the request never reached
  // the API; a JSON body can still carry a failure envelope.
  const text = (await response.text().catch(() => "")).trim();
  if (text.startsWith("<")) {
    throw new Error("Couldn't delete this item. Please try again.");
  }
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    /* non-JSON, non-HTML body — fall through to envelope check with null */
  }
  const failure = envelopeFailureMessage(data);
  if (failure) throw new Error(failure);
}

/**
 * Renames a Gallery section across every item currently filed under it. 
 */
async function renameGallerySection(
  businessId: string,
  currentSectionName: string,
  newSectionName: string
): Promise<void> {
  const endpoint = `/api/v1/business-images/${businessId}/gallery/rename-section`;
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() =>
    putRequest(endpoint, { currentSectionName, newSectionName }, includeBearerToken)
  );
  if (!response.ok) {
    throw new Error(await statusToMessage(response, "Failed to rename section"));
  }
  const data = await response.json().catch(() => null);
  const failure = envelopeFailureMessage(data);
  if (failure) throw new Error(failure);
}

/**
 * Maps an HTTP error response to a user-facing message. Used when the response
 * isn't a parseable JSON envelope (e.g. 413 returns an nginx HTML page).
 */
async function statusToMessage(response: Response, fallback: string): Promise<string> {
  if (response.status === 413) return "File is too large. Please choose a smaller image.";
  if (response.status === 415) return "This file type isn't supported.";
  if (response.status === 403) return "You don't have permission to do this.";
  if (response.status === 404) return "Couldn't find this business.";
  if (response.status >= 500) return "Something went wrong on our end. Please try again.";

  // Fall back to whatever the body says, if it's JSON
  const data = await response.json().catch(() => null);
  return extractErrorMessage(data) || fallback;
}

async function getBusinessesByUserId(userId: string) {
  const endpoint = `/api/v1/business/get-businesses-by-userid/${userId}`;
  const includeBearerToken = false;
  const response = await getRequest(endpoint, includeBearerToken);
  const data = await response.json();
  return convertRawBusinessList(data);
}

// /get-other-user-business/{userId} is the route to get businesses of other user
async function getOtherUserBusinesses(userId: string) {
  const endpoint = `/api/v1/business/get-other-user-businesses/${userId}`;
  const includeBearerToken = false;
  const response = await getRequest(endpoint, includeBearerToken);
  const data = await response.json();
  return convertRawBusinessList(data);
}

async function createBusiness(business: CreateBusinessPayload) {
  const endpoint = "/api/v1/business/create-business";
  const payload = buildCreateBusinessPayload(business);
  const includeBearerToken = true;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  const data = await response.json();
  const created = data?.contents?.[0];
  if (data?.generalProcessingStatus?.processingStatus !== "OK" || !created) {
    throw new Error(
      data?.generalProcessingStatus?.processingDetails || "Failed to create business"
    );
  }
  return convertRawBusiness(created);
}


async function updateBusiness(payload: UpdateBusinessPayload) {
  const endpoint = `/api/v1/business/update-business/${payload.businessId}`;
  const body = buildUpdateBusinessPayload(payload);
  const includeBearerToken = true;
  const response = await putRequest(endpoint, body, includeBearerToken);
  const data = await response.json();
  const updated = data?.contents?.[0];
  if (data?.generalProcessingStatus?.processingStatus !== "OK" || !updated) {
    throw new Error(
      data?.generalProcessingStatus?.processingDetails || "Failed to update business"
    );
  }
  return convertRawBusiness(updated);
}

async function deleteBusiness(businessId: string): Promise<void> {
  const endpoint = `/api/v1/business/delete-business/${businessId}`;
  const includeBearerToken = true;
  const response = await deleteRequest(endpoint, includeBearerToken);
  if (!response.ok) {
    throw new Error(await statusToMessage(response, "Failed to delete business"));
  }
}

async function updateBusinessMoreDetails(
  businessId: string,
  moreDetails: BusinessMoreDetails
): Promise<BusinessMoreDetails | null> {
  const endpoint = `/api/v1/business/${businessId}/more-details`;
  const includeBearerToken = true;
  const response = await putRequest(endpoint, { moreDetails }, includeBearerToken);
  const data = await response.json();
  if (data?.generalProcessingStatus?.processingStatus !== "OK") {
    throw new Error(
      data?.generalProcessingStatus?.processingDetails || "Failed to update details"
    );
  }
  // Use the server's assembled view as the source of truth — it reflects the
  // post-save state (deletes applied, untouched keys preserved).
  return data?.contents?.[0]?.moreDetails ?? null;
}

/**
 * The OWNER signs off a page built for them through the Nine Setup pipeline —
 * `PUT /api/v1/business/mark-onboarded/{id}`. Flips `isOnboarded` false → true.
 *
 * Migrated 2026-08-28 off `POST .../confirm-onboarding`, which doesn't exist in
 * the user service — the real route is this PUT.
 *
 * Sends no body: the only fact being recorded is "the owner pressed the button",
 * and the caller's identity comes from the bearer token. The checklist in front
 * of it is a prompt to actually look, not data the server wants — nothing is
 * gained by shipping four booleans that can only ever all be true.
 *
 * Only the OWNER (not a collaborator) may call this; a non-owner gets back
 * `NOT_OWNER` rather than a generic 403 — see BUSINESS_ONBOARDED_ERROR_CODES.
 * A second call (double-tap, race) is rejected as `BUSINESS_ALREADY_ONBOARDED`
 * — that's the server saying onboarding already happened, not a failure, so
 * callers should treat it as a success no-op rather than alarming the owner.
 *
 * Resolves to the updated business (from `contents[0]`, already reflecting
 * `isOnboarded: true` — no follow-up fetch needed) so the caller can drop the
 * prompt without a refetch, and tolerates a server that answers with an empty
 * envelope.
 */
async function markBusinessOnboarded(
  businessId: string
): Promise<BusinessProfile | null> {
  const endpoint = `${ServiceEndpoints.BUSINESS_BY_ID}/mark-onboarded/${businessId}`;
  const includeBearerToken = true;
  const response = await fetchOrThrowFriendly(() => putRequest(endpoint, {}, includeBearerToken));
  const data = await response.json().catch(() => null);

  // The backend reports the real outcome in the body envelope even on a 2xx,
  // so check both the HTTP status and the envelope before trusting success.
  const status = data?.generalProcessingStatus?.status;
  const failed =
    !response.ok ||
    (typeof status === "string" && status !== "OK" && status !== "SUCCESS");

  if (failed) {
    const code =
      data?.errorInformation?.[0]?.code ??
      data?.errorInformation?.[0]?.errorCode;

    // Already onboarded (double-submit / race): the server is saying the
    // first call already succeeded, not that this one failed.
    if (code === BUSINESS_ONBOARDED_ERROR_CODES.ALREADY_ONBOARDED) {
      const raw = data?.contents?.[0];
      return raw ? convertRawBusiness(raw) : null;
    }

    // Body's already been read into `data` above, so build the message from
    // that rather than calling statusToMessage (which re-reads response.json()
    // and would throw on an already-consumed stream).
    const fallback =
      response.status === 403
        ? "You don't have permission to do this."
        : response.status === 404
        ? "Couldn't find this business."
        : response.status >= 500
        ? "Something went wrong on our end. Please try again."
        : "Couldn't confirm your onboarding";
    const message =
      code === BUSINESS_ONBOARDED_ERROR_CODES.NOT_OWNER
        ? "Only the business owner can confirm onboarding."
        : extractErrorMessage(data) ||
          data?.generalProcessingStatus?.message ||
          fallback;
    const err = new Error(message);
    if (code) (err as Error & { code?: string }).code = code;
    throw err;
  }

  const raw = data?.contents?.[0];
  return raw ? convertRawBusiness(raw) : null;
}

/**
 * `mark-onboarded`'s 14xxx error family. Exact literal strings unverified
 * against a live call as of 2026-08-28 (the DTO/enum source lives in a
 * submodule not checked out in this workspace) — confirm before relying on
 * any code other than these two branches.
 */
const BUSINESS_ONBOARDED_ERROR_CODES = {
  NOT_OWNER: "NOT_OWNER",
  ALREADY_ONBOARDED: "BUSINESS_ALREADY_ONBOARDED",
} as const;

// Back-compat alias — keep call sites working while anything else in the
// codebase still imports the old name.
const confirmOnboarding = markBusinessOnboarded;

// ─── Customers ──────────────────────────────────────────────────────────────

/**
 * Phone numbers of customers who ordered from this business.
 * Owner/collaborator-only on the backend (Bearer token required).
 * Normalizes `contents` whether the backend sends plain strings or
 * objects carrying a phone field.
 */
async function getCustomersForBusiness(businessId: string): Promise<string[]> {
  const endpoint = `/api/v1/business/get-customers-for-business/${businessId}`;
  const response = await getRequest(endpoint, true);
  if (!response.ok) {
    throw new Error(await statusToMessage(response, "Failed to load customers"));
  }
  const data = await response.json();
  const contents: unknown = data?.contents;
  if (!Array.isArray(contents)) {
    throw new Error(extractErrorMessage(data) || "Failed to load customers");
  }
  // Backend returns contents as [[{...}]] (array of arrays) — flatten one level
  const flat: unknown[] = contents.flatMap((item: unknown) =>
    Array.isArray(item) ? item : [item]
  );
  return flat
    .map((c: any) =>
      typeof c === "string"
        ? c
        : c?.customerPhoneNumber ?? c?.phone ?? c?.phoneNumber ?? c?.customerPhone ?? ""
    )
    .map((s: unknown) => String(s).trim())
    .filter(Boolean);
}

/**
 * Links a customer to a business by phone number.
 * Returns the created customer-business relationship record.
 */
async function addCustomerToBusiness(
  businessId: string,
  customerPhoneNumber: string
): Promise<{ customerId: string; businessId: string; customerPhoneNumber: string }> {
  const endpoint = `/api/v1/business/add-customer-to-business/${encodeURIComponent(businessId)}?customerPhoneNumber=${encodeURIComponent(customerPhoneNumber)}`;
  const response = await postRequest(endpoint, {}, false);
  if (!response.ok) {
    throw new Error(await statusToMessage(response, "Failed to add customer to business"));
  }
  const data = await response.json();
  const record = data?.contents?.[0];
  if (!record) {
    throw new Error(extractErrorMessage(data) || "Failed to add customer to business");
  }
  return record;
}

// ─── Ratings ────────────────────────────────────────────────────────────────

async function submitBusinessRating(
  payload: SubmitRatingPayload
): Promise<BusinessRating> {
  const endpoint = "/api/v1/business/post-rating";
  const body = {
    businessId: payload.businessId,
    rating: payload.rating,
    ...(payload.review ? { review: payload.review } : {}),
  };
  // X-Device-Id identifies the rater when logged out (the backend ignores it when a JWT is present).
  const response = await postRequest(endpoint, body, true, {
    "X-Device-Id": AnonymousRatingHelper.getDeviceId(),
  });
  if (!response.ok) {
    throw new Error(await statusToMessage(response, "Failed to submit rating"));
  }
  const data = await response.json();
  const raw = data?.contents?.[0];
  if (data?.generalProcessingStatus?.processingStatus !== "OK" || !raw) {
    throw new Error(extractErrorMessage(data) || "Failed to submit rating");
  }
  return convertRawBusinessRating(raw);
}

async function getBusinessRatings(
  businessId: string
): Promise<BusinessRatingsSummary> {
  const endpoint = `/api/v1/business/get-rating-by-business-id?businessId=${encodeURIComponent(businessId)}`;
  const response = await getRequest(endpoint, true);
  if (!response.ok) {
    throw new Error(await statusToMessage(response, "Failed to load ratings"));
  }
  const data = await response.json();
  const raw = data?.contents?.[0];
  if (!raw) {
    // No ratings yet — return an empty summary rather than throwing.
    return { avgRating: 0, totalRatings: 0, ratings: [] };
  }
  return convertRawBusinessRatingsSummary(raw);
}

async function deleteBusinessRating(ratingId: string): Promise<void> {
  const endpoint = `/api/v1/business/delete-rating-by-rating-id?ratingId=${encodeURIComponent(ratingId)}`;
  const includeBearerToken = true;
  const response = await deleteRequest(endpoint, includeBearerToken, {
    "X-Device-Id": AnonymousRatingHelper.getDeviceId(),
  });
  if (!response.ok) {
    throw new Error(await statusToMessage(response, "Failed to delete rating"));
  }
}

const BusinessApiService = {
  getMyBusinesses,
  businessSearch,
  searchCollectionsByName,
  getCollectionById,
  getMyCollections,
  createCollection,
  updateCollection,
  deleteCollection,
  getBusinessById,
  getBusinessListings,
  getOtherUserBusinesses,
  getBusinessesByUserId,
  createBusiness,
  updateBusiness,
  deleteBusiness,
  getBusinessMedia,
  appendBusinessMedia,
  deleteBusinessMedia,
  renameGallerySection,
  updateBusinessMoreDetails,
  confirmOnboarding, // deprecated alias — use markBusinessOnboarded
  markBusinessOnboarded,
  getCustomersForBusiness,
  addCustomerToBusiness,
  submitBusinessRating,
  getBusinessRatings,
  deleteBusinessRating,
};

export default BusinessApiService;
